/**
 * Số điện thoại Việt Nam: chuẩn hoá, hiển thị, và **che**.
 *
 * Một dạng duy nhất trong toàn hệ thống: E.164 `+84` + 9 số. `staff.phone`, `customer.phone`,
 * `staff_login.phone` đều dùng dạng này, và cơ sở dữ liệu có CHECK để không ai lách.
 *
 * Vì sao che số ở đây (tầng dùng chung) chứ không ở giao diện: che ở giao diện thì mở DevTools là
 * thấy đủ. Số điện thoại khách là tài sản của tiệm — công tắc "kỹ thuật viên có thấy số khách
 * không" phải có tác dụng thật, nghĩa là API trả về số đã che, không phải số đầy đủ kèm một lời
 * nhắn "đừng hiện".
 */

/**
 * Bảng đổi đầu số di động 11 chữ số → 10 chữ số (khoá = 3 chữ số sau số 0 đầu của số CŨ; giá trị = 2 chữ số
 * sau số 0 của số MỚI). Số cũ `0` + khoá + 7 số → số mới `0` + giá trị + 7 số (7 số cuối giữ nguyên).
 *
 * NGUỒN (kiểm ngày 11/10/2026, hạng mục 2.2 Đợt 2 — kế hoạch `docs/plans/stt-2-1-2-2-khach-nhap.md` mục 4.2):
 *  - Quyết định 798/QĐ-BTTTT ngày 25/5/2018 của Bộ Thông tin và Truyền thông, "Kế hoạch chuyển đổi mã mạng
 *    thuê bao di động 11 số sang 10 số" (21 mã mạng, 5 doanh nghiệp). Bảng trích từ bản đăng trên
 *    https://luatvietnam.vn/thong-tin/quyet-dinh-798-qd-btttt-2018-ke-hoach-chuyen-thue-bao-11-so-sang-10-so-163661-d1.html
 *    (trang thuvienphapluat.vn trả 403 khi tra từ máy chủ, chưa mở được bản gốc của Bộ — nên đối chiếu thêm các nguồn dưới);
 *  - bảng tổng hợp theo nhà mạng, VietNamNet 31/05/2018
 *    https://vietnamnet.vn/bang-tong-hop-doi-11-so-thanh-10-so-cac-nha-mang-i375066.html ;
 *  - Wikipedia tiếng Việt "Mã điện thoại Việt Nam" (dẫn QĐ 798/QĐ-BTTTT).
 * Cả ba khớp nhau từng dòng. Chuyển đổi bắt đầu 15/9/2018; từ 2019 số 11 chữ số không còn dùng — chỉ còn
 * xuất hiện trong SỔ KHÁCH CŨ của tiệm, nên bảng này chỉ dùng để đọc lại số đã ghi từ trước.
 */
export const BANG_DAU_SO_CU: Readonly<Record<string, string>> = {
  // Viettel
  '162': '32',
  '163': '33',
  '164': '34',
  '165': '35',
  '166': '36',
  '167': '37',
  '168': '38',
  '169': '39',
  // MobiFone
  '120': '70',
  '121': '79',
  '122': '77',
  '126': '76',
  '128': '78',
  // VinaPhone
  '123': '83',
  '124': '84',
  '125': '85',
  '127': '81',
  '129': '82',
  // Vietnamobile
  '186': '56',
  '188': '58',
  // Gmobile
  '199': '59',
}

export interface KetQuaSdtChiTiet {
  /** `+84` + 9 chữ số, hoặc `null` nếu không nhận dạng được. */
  so: string | null
  /** Số 11 chữ số cũ (đầu 01x) đã được đổi sang đầu số mới. */
  doiDauSo: boolean
  /** Số cũ bị mất số 0 ở đầu (Excel) — đã ĐOÁN lại. Chỉ bật khi người gọi cho phép (`nhanThieuSo0Cu`). */
  doanThieuSo0: boolean
}

/** Chữ số toàn khổ (０-９) → ASCII; mọi ký tự khác giữ nguyên. */
function chuSoToanKho(s: string): string {
  let ra = ''
  for (const ch of s) {
    const cp = ch.codePointAt(0) ?? 0
    ra += cp >= 0xff10 && cp <= 0xff19 ? String.fromCharCode(cp - 0xff10 + 0x30) : ch
  }
  return ra
}

/**
 * Như `chuanHoaSdtVn` nhưng nói rõ số có phải số 11 chữ số cũ đã đổi đầu số hay không.
 *
 * Nhận thêm (so với 9/10 chữ số cũ): số 11 chữ số đầu 01 theo `BANG_DAU_SO_CU` (`01689123456`,
 * `+84 168 912 3456`, `841689123456`, `+84 0168…`, `0084…`). Số 10 chữ số đầu `1` KHÔNG tiền tố
 * (`1689123456` — Excel làm mất số 0 đầu) chỉ được đoán lại khi `nhanThieuSo0Cu` — dùng ở đường nhập
 * danh sách, nơi người xem trước được nhìn số đã hiểu; các đường khác (đăng nhập, tìm khách…) vẫn từ chối.
 */
export function chuanHoaSdtVnChiTiet(
  raw: string,
  o: { nhanThieuSo0Cu?: boolean } = {},
): KetQuaSdtChiTiet {
  const khong: KetQuaSdtChiTiet = { so: null, doiDauSo: false, doanThieuSo0: false }
  if (typeof raw !== 'string') return khong
  let d = chuSoToanKho(raw).replace(/\D/g, '')
  let coTienTo = false
  if (d.startsWith('0084')) {
    d = d.slice(4)
    coTienTo = true
  }
  let n: string
  if (d.startsWith('84') && d.length >= 11) {
    n = d.slice(2)
    // "+84 0912 345 678": người ta hay gõ số 0 nội địa ngay sau mã quốc gia.
    if (n.startsWith('0')) n = n.slice(1)
    coTienTo = true
  } else if (d.startsWith('0')) {
    n = d.slice(1)
    coTienTo = true
  } else n = d
  // Số di động / cố định thuê bao: 9 chữ số sau số 0, đầu 2, 3, 5, 7, 8, 9. Đầu 0, 1, 4, 6 không có thật
  // ("0098 733 225" từng lọt qua ở quầy đặt lịch, 30/09).
  if (n.length === 9 && /^[235789]/.test(n)) return { ...khong, so: `+84${n}` }
  // Số 11 chữ số cũ: `0` + 3 số đầu + 7 số = 10 chữ số sau khi bỏ số 0.
  if (n.length === 10 && n.startsWith('1') && (coTienTo || o.nhanThieuSo0Cu === true)) {
    const moi = BANG_DAU_SO_CU[n.slice(0, 3)]
    if (moi !== undefined) {
      return { so: `+84${moi}${n.slice(3)}`, doiDauSo: true, doanThieuSo0: !coTienTo }
    }
  }
  return khong
}

/**
 * Đưa mọi cách người ta gõ về một dạng. Trả `null` nếu không nhận dạng được — người gọi quyết
 * định đó là lỗi nhập liệu (400) hay là "không tìm thấy", chứ hàm này không ném lỗi.
 *
 * Nhận: `0901000296` · `0901 000 296` · `(090) 100-0296` · `+84901000296` · `84901000296` ·
 * `901000296` · số 11 chữ số cũ `01689123456` (đổi sang `+84389123456`).
 */
export function chuanHoaSdtVn(raw: string): string | null {
  return chuanHoaSdtVnChiTiet(raw).so
}

/** `+84901000296` -> `0901 000 296`. Dạng lễ tân đọc to cho khách nghe. */
export function hienSdtVn(phone: string | null | undefined): string {
  if (phone === null || phone === undefined || phone === '') return ''
  const noiBo = phone.startsWith('+84') ? `0${phone.slice(3)}` : phone
  if (!/^0\d{9}$/.test(noiBo)) return phone
  return `${noiBo.slice(0, 4)} ${noiBo.slice(4, 7)} ${noiBo.slice(7)}`
}

/**
 * Che giữa số: `+84901000296` -> `0901***296`.
 *
 * Giữ 4 số đầu và 3 số cuối. Vì sao giữ lại chừng đó: kỹ thuật viên cần **nhận ra** khách quen khi
 * nhìn bảng ca ("à, chị 296"), nhưng không được gọi hay nhắn cho khách ngoài hệ thống. Che sạch
 * thì bảng ca vô dụng; giữ nhiều quá thì đoán ra số thật.
 */
/**
 * Che mọi dãy ≥ 9 chữ số trong CHỮ TỰ DO — lời khách nhắn, ghi chú buổi, lý do, "Lưu ý cố định", tên: khách hay gõ
 * lại số của mình vào đó ("gọi mình 0912 345 678"). Dùng khi người xem không có quyền thấy số (RETRO bảo mật 8, 24/09).
 *
 * Che Ở LÚC ĐỌC (không dựa vào việc đường ghi đã chuẩn hoá) nên phủ cả dữ liệu cũ. Phải nhận mọi cách người ta
 * viết một số (soát bảo mật 2.1/2.2, 11/10): chữ số toàn khổ / của chữ viết khác (`０９８７…`, `٠٩٨٧…`) gập về ASCII;
 * giữa hai chữ số được xen tối đa 3 ký tự KHÔNG phải chữ / số (`/ _ , – — . - ( )`…), hoặc 4–8 ký tự trộn khoảng
 * trắng + dấu câu (`0912  -  345`, `0912....345`), hoặc một chuỗi khoảng trắng bất kỳ dài (ba nhánh có độ dài rời nhau → vẫn tuyến tính); dấu kết hợp (zalgo) và ký tự định dạng ẩn (độ rộng 0) bị bỏ qua. Giữ ngưỡng ≥ 9 chữ số nên số tiền
 * (`300.000đ`), ngày (`12/05/1985`), giờ, mã ngắn không bị che.
 */
const DAY_CHU_SO = /\+?\d(?:(?:[^\p{L}\p{N}]{0,3}|[\s\p{P}]{4,8}|\s{9,})\d){8,}/gu
const CO_CHU_SO_UNICODE = /\p{N}/u
const KY_TU_BO_QUA = /^[\p{M}\p{Cf}]$/u
const CHU_SO_KHAC = /^\p{Nd}$/u

/** Một ký tự → MỘT ký tự dùng để so khớp (chữ số mọi loại → `0`, dạng toàn khổ → ASCII, chữ cái → `a`). */
function gapMotKyTu(c: string): string {
  if (CHU_SO_KHAC.test(c)) return '0'
  const n = c.normalize('NFKC')
  if (n.length === 1) return n
  if (/^\d+$/.test(n)) return '0'
  return /^\p{L}/u.test(n) ? 'a' : '\uFFFF'
}

/**
 * Gập chuỗi để TÌM số (không để hiển thị): mỗi ký tự gốc → một ký tự gập; dấu kết hợp và ký tự định dạng ẩn bị
 * bỏ. `goc[i]` = chỉ số (theo ký tự Unicode) của ký tự gốc ứng với ký tự gập thứ i.
 */
function gapDeTimSo(s: string): { f: string; goc: number[]; cps: string[] } {
  const cps = Array.from(s)
  let f = ''
  const goc: number[] = []
  cps.forEach((c, i) => {
    if (KY_TU_BO_QUA.test(c)) return
    f += gapMotKyTu(c)
    goc.push(i)
  })
  return { f, goc, cps }
}

/** Che mọi số trong chữ tự do, thay bằng `thay`. `cheSoTrongChu` (quầy, log) dùng `***`; câu cho KHÁCH dùng chữ đọc được. */
function cheSoVoiChuThay(s: string, thay: string): string {
  if (!CO_CHU_SO_UNICODE.test(s)) return s
  const { f, goc, cps } = gapDeTimSo(s)
  let ra = ''
  let tu = 0 // chỉ số ký tự gốc đã chép tới đâu
  for (const m of f.matchAll(DAY_CHU_SO)) {
    const a = goc[m.index] as number
    const b = (goc[m.index + m[0].length - 1] as number) + 1
    if (a < tu) continue
    ra += `${cps.slice(tu, a).join('')}${thay}`
    tu = b
  }
  if (tu === 0) return s
  return ra + cps.slice(tu).join('')
}

export function cheSoTrongChu(s: string): string {
  return cheSoVoiChuThay(s, '***')
}

/**
 * Chữ thay cho một số lạ bị che, trong câu HIỆN CHO KHÁCH ("Gọi chị Hoa (số điện thoại đã ẩn) nhé"): `***` trên màn
 * khách trông như lỗi hiển thị (K1a vòng sửa 2).
 */
export const CHU_SO_DA_AN = '(số điện thoại đã ẩn)'

/** Cụm chữ thay cho số điện thoại CHÍNH CỦA CHI NHÁNH trong chữ tự do (K1a vòng sửa 2). */
export const CHU_SO_CUA_TIEM = 'số của cửa hàng'

/** Giá trị 0–9 của một ký tự chữ số (mọi loại chữ số Unicode); không phải chữ số → ''. */
function giaTriChuSo(c: string): string {
  const n = c.normalize('NFKC')
  if (/^[0-9]$/.test(n)) return n
  if (!CHU_SO_KHAC.test(c)) return ''
  // Mỗi bộ chữ số Unicode là 10 ký tự liền nhau: giá trị = khoảng cách tới đầu dãy, theo mod 10.
  const cp = c.codePointAt(0) ?? 0
  let dau = cp
  while (dau > 0 && CHU_SO_KHAC.test(String.fromCodePoint(dau - 1))) dau--
  return String((cp - dau) % 10)
}

/**
 * Chữ liền trước một số ghi rõ tên cửa hàng ("cửa hàng", "số cửa hàng", "số của cửa hàng") — gộp với CHU_SO_CUA_TIEM
 * để khỏi lặp. Vẫn nhận chữ "tiệm" vì chủ gõ trong ghi chú / lý do bằng chữ của web quầy.
 */
// chu-quay: chữ chủ cửa hàng gõ ở web quầy vẫn có thể là "tiệm"
const TRUOC_LA_TIEM = /(?<![\p{L}\p{N}])(?:số\s+(?:của\s+)?)?(?:tiệm|cửa\s+hàng)[\s:,\-–]*$/iu

/**
 * Như `cheSoTrongChu`, nhưng DÀNH CHO CÂU HIỆN CHO KHÁCH: số điện thoại CHÍNH CỦA CHI NHÁNH (mọi cách viết, so theo
 * số đã chuẩn hoá) được thay bằng "số của tiệm" thay vì `***` — khách vốn đã thấy số này (nút "Gọi tiệm"), và câu
 * "Gọi *** để…" thì cụt; khi chữ liền trước đã là "tiệm" thì gộp ("Gọi tiệm 090…" → "Gọi số của tiệm", không "tiệm
 * số của tiệm"). Mọi số khác bị che bằng `CHU_SO_DA_AN` ("(số điện thoại đã ẩn)"). `soTiem` rỗng / không nhận dạng
 * được → chỉ che.
 */
export function cheSoTrongChuTruSoTiem(s: string, soTiem: string | null | undefined): string {
  const soChuan = soTiem === null || soTiem === undefined ? null : chuanHoaSdtVn(soTiem)
  if (soChuan === null || !CO_CHU_SO_UNICODE.test(s)) return cheSoVoiChuThay(s, CHU_SO_DA_AN)
  const { f, goc, cps } = gapDeTimSo(s)
  let ra = ''
  let tu = 0
  for (const m of f.matchAll(DAY_CHU_SO)) {
    const a = goc[m.index] as number
    const b = (goc[m.index + m[0].length - 1] as number) + 1
    if (a < tu) continue
    const chuSo = cps.slice(a, b).map(giaTriChuSo).join('')
    if (chuanHoaSdtVn(`${m[0].startsWith('+') ? '+' : ''}${chuSo}`) !== soChuan) continue
    const truoc = cps.slice(tu, a).join('')
    const lap = TRUOC_LA_TIEM.exec(truoc)
    if (lap === null) {
      ra += `${truoc}${CHU_SO_CUA_TIEM}`
    } else {
      // Chữ "tiệm" đứng ngay trước: một cụm "số của tiệm" thay cho cả "tiệm <số>" (giữ chữ hoa đầu câu).
      const dau = lap[0].charAt(0)
      const hoa = dau !== dau.toLowerCase()
      const cum = hoa ? `S${CHU_SO_CUA_TIEM.slice(1)}` : CHU_SO_CUA_TIEM
      ra += `${truoc.slice(0, lap.index)}${cum}`
    }
    tu = b
  }
  if (tu === 0) return cheSoVoiChuThay(s, CHU_SO_DA_AN)
  return cheSoVoiChuThay(ra + cps.slice(tu).join(''), CHU_SO_DA_AN)
}

export function cheSdtVn(phone: string | null | undefined): string | null {
  if (phone === null || phone === undefined || phone === '') return null
  const noiBo = phone.startsWith('+84') ? `0${phone.slice(3)}` : phone
  if (!/^0\d{9}$/.test(noiBo)) return '***'
  return `${noiBo.slice(0, 4)}***${noiBo.slice(7)}`
}
